import * as THREE from 'three';
import type { Level } from '../../core/hp/types';
import type { ParticipantSelection } from '../../collaboration/selection';
import { SelectionPresentation } from './SelectionPresentation';
import { SurfaceSelection, roomEdgeInsets } from './SurfaceSelection';

/** Participant outlines share the editor's geometry-following selection, without selecting or editing it. */
export class ParticipantSelections {
  private entries = new Map<string, { value: ParticipantSelection; outline: SelectionPresentation | SurfaceSelection }>();
  constructor(private readonly scene: THREE.Scene) {}

  set(values: readonly ParticipantSelection[]): boolean {
    const keep = new Set(values.map(value => value.key));
    let changed = false;
    for (const [key, entry] of this.entries) if (!keep.has(key)) {
      entry.outline.dispose(); this.entries.delete(key); changed = true;
    }
    for (const value of values) {
      const entry = this.entries.get(value.key);
      if (entry?.value.color === value.color) { entry.value = value; continue; }
      entry?.outline.dispose();
      const outline = value.target.kind === 'wall' || value.target.kind === 'room'
        ? new SurfaceSelection(value.color) : new SelectionPresentation(value.color);
      outline.root.name = `participant-selection:${value.key}`;
      this.entries.set(value.key, { value, outline }); this.scene.add(outline.root); changed = true;
    }
    return changed;
  }

  update(level: Level | null | undefined, nodeFor: (key: string) => THREE.Object3D | undefined, enabled: boolean): void {
    for (const { value: { target }, outline } of this.entries.values()) {
      const key = target.kind === 'room' ? `floor:${target.index}` : `${target.kind}:${target.id}`;
      const node = nodeFor(key);
      if (!level || !node) { outline.clear(); continue; }
      if (outline instanceof SurfaceSelection) {
        const wall = target.kind === 'wall' ? level.walls.find(wall => wall.id === target.id) : null;
        const room = target.kind === 'room' ? level.rooms.find(room => room.index === target.index) : null;
        outline.update(wall ? { kind: 'wall', node, wall } : room ? { kind: 'room', node, polygon: room.polygon,
          insets: roomEdgeInsets(room.polygon, level.walls) } : null, enabled);
      } else {
        const item = target.kind === 'item' ? level.items.find(item => item.id === target.id) : null;
        outline.update(node, { enabled, model: !item || item.modelRef.kind !== 'none' });
      }
    }
  }

  dispose(): void { for (const entry of this.entries.values()) entry.outline.dispose(); this.entries.clear(); }
}
